import { FormEvent, useEffect, useState } from 'react';
import AdminLayout from '../AdminLayout';
import { fetchAdminCompanyHistory, saveCompanyHistory } from '../adminService';
import { useAdminSession } from '../adminSession';
import { ApiError } from '../../../services/apiClient';
import { CompanyHistoryContent, DEFAULT_COMPANY_HISTORY } from '../../Company/companyHistoryService';
import formStyles from '../Portfolio/AdminPortfolioForm.module.scss';
import listStyles from '../Portfolio/AdminPortfolio.module.scss';
import styles from './AdminCompanyHistory.module.scss';

// 편집 중에는 목록을 줄바꿈 텍스트로 들고 있다가 저장할 때 배열로 바꾼다
interface GroupDraft {
  label: string;
  itemsText: string;
}

interface ServiceDraft {
  title: string;
  desc: string;
}

interface Draft {
  badgesText: string;
  introText: string;
  history: GroupDraft[];
  ceoName: string;
  ceoTitle: string;
  career: GroupDraft[];
  services: ServiceDraft[];
}

const toLines = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

function toDraft(content: CompanyHistoryContent): Draft {
  const toGroups = (groups: CompanyHistoryContent['history']) =>
    groups.map((g) => ({ label: g.label, itemsText: g.items.join('\n') }));
  return {
    badgesText: content.intro.badges.join('\n'),
    introText: content.intro.text.join('\n'),
    history: toGroups(content.history),
    ceoName: content.ceo.name,
    ceoTitle: content.ceo.title,
    career: toGroups(content.ceo.career),
    services: content.services.map((s) => ({ ...s })),
  };
}

function fromDraft(draft: Draft): CompanyHistoryContent {
  const fromGroups = (groups: GroupDraft[]) =>
    groups.filter((g) => g.label.trim()).map((g) => ({ label: g.label.trim(), items: toLines(g.itemsText) }));
  return {
    intro: { badges: toLines(draft.badgesText), text: toLines(draft.introText) },
    history: fromGroups(draft.history),
    ceo: { name: draft.ceoName.trim(), title: draft.ceoTitle.trim(), career: fromGroups(draft.career) },
    services: draft.services
      .filter((s) => s.title.trim())
      .map((s) => ({ title: s.title.trim(), desc: s.desc.trim() })),
  };
}

function move<T>(list: T[], index: number, offset: -1 | 1): T[] {
  const next = [...list];
  [next[index], next[index + offset]] = [next[index + offset], next[index]];
  return next;
}

function RowButtons({
  index,
  length,
  onMove,
  onRemove,
}: {
  index: number;
  length: number;
  onMove: (offset: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div className={listStyles.rowActions}>
      <button type="button" className="btn btn-outline" aria-label="위로" disabled={index === 0} onClick={() => onMove(-1)}>
        ↑
      </button>
      <button
        type="button"
        className="btn btn-outline"
        aria-label="아래로"
        disabled={index === length - 1}
        onClick={() => onMove(1)}
      >
        ↓
      </button>
      <button type="button" className={`btn btn-outline ${listStyles.dangerBtn}`} onClick={onRemove}>
        삭제
      </button>
    </div>
  );
}

// 제목 + 항목 목록 묶음 편집 (회사 연혁, 대표이사 이력 공통)
function GroupListEditor({
  groups,
  onChange,
  labelPlaceholder,
}: {
  groups: GroupDraft[];
  onChange: (groups: GroupDraft[]) => void;
  labelPlaceholder: string;
}) {
  const update = (index: number, patch: Partial<GroupDraft>) =>
    onChange(groups.map((g, i) => (i === index ? { ...g, ...patch } : g)));

  return (
    <div className={styles.groupList}>
      {groups.map((group, index) => (
        <div key={index} className={styles.groupRow}>
          <input
            value={group.label}
            maxLength={100}
            placeholder={labelPlaceholder}
            onChange={(e) => update(index, { label: e.target.value })}
          />
          <textarea
            rows={Math.min(Math.max(group.itemsText.split('\n').length, 3), 14)}
            value={group.itemsText}
            placeholder="한 줄에 하나씩 입력"
            onChange={(e) => update(index, { itemsText: e.target.value })}
          />
          <RowButtons
            index={index}
            length={groups.length}
            onMove={(offset) => onChange(move(groups, index, offset))}
            onRemove={() => {
              if (window.confirm(`"${group.label || '제목 없음'}" 묶음을 삭제하시겠습니까?`)) {
                onChange(groups.filter((_, i) => i !== index));
              }
            }}
          />
        </div>
      ))}
      <button
        type="button"
        className={`btn btn-outline ${styles.addBtn}`}
        onClick={() => onChange([...groups, { label: '', itemsText: '' }])}
      >
        + 묶음 추가
      </button>
    </div>
  );
}

// 회사소개 > 연혁(/company/history) 페이지 문구 관리
function AdminCompanyHistory() {
  const [session, setSession] = useAdminSession();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [isSaved, setIsSaved] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.status === 401) {
      setSession(null);
    } else {
      setError(err instanceof Error ? err.message : fallback);
    }
  };

  useEffect(() => {
    if (!session) return;
    fetchAdminCompanyHistory(session.token)
      .then((res) => {
        setDraft(toDraft(res.content ?? DEFAULT_COMPANY_HISTORY));
        setIsSaved(!!res.content);
      })
      .catch((err) => handleError(err, '연혁 내용을 불러오지 못했습니다.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const setField = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setNotice(null);
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!session || !draft) return;
    setError(null);
    setNotice(null);
    setIsBusy(true);
    try {
      const res = await saveCompanyHistory(session.token, fromDraft(draft));
      setDraft(toDraft(res.content));
      setIsSaved(true);
      setNotice('저장했습니다. 연혁 페이지에 바로 반영됩니다.');
    } catch (err) {
      handleError(err, '저장 중 오류가 발생했습니다.');
    } finally {
      setIsBusy(false);
    }
  };

  const updateService = (index: number, patch: Partial<ServiceDraft>) =>
    draft && setField('services', draft.services.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  return (
    <AdminLayout title="연혁 관리">
      {error && (
        <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
          {error}
        </div>
      )}
      {!draft ? (
        !error && <div className="card">불러오는 중...</div>
      ) : (
        <form className={formStyles.form} onSubmit={handleSubmit}>
          {!isSaved && (
            <div className="card">
              아직 저장된 내용이 없어 현재 페이지에 보이는 기본 문구를 불러왔습니다. 수정 후 저장하면 그때부터 반영됩니다.
            </div>
          )}

          <div className="section">
            <div className="sec-head">
              <div className="sec-title">회사 소개</div>
            </div>
            <div className="card">
              <div className={formStyles.grid}>
                <div className={formStyles.field}>
                  <label>배지</label>
                  <textarea
                    rows={6}
                    value={draft.badgesText}
                    placeholder="한 줄에 하나씩 입력"
                    onChange={(e) => setField('badgesText', e.target.value)}
                  />
                </div>
                <div className={`${formStyles.field}`} style={{ gridColumn: 'span 2' }}>
                  <label>소개 문구</label>
                  <textarea
                    rows={6}
                    value={draft.introText}
                    placeholder="줄바꿈한 위치 그대로 표시됩니다"
                    onChange={(e) => setField('introText', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="sec-head">
              <div className="sec-title">회사 연혁</div>
            </div>
            <div className="card">
              <GroupListEditor
                groups={draft.history}
                onChange={(groups) => setField('history', groups)}
                labelPlaceholder="예: 디자인 · 제작"
              />
              <p className={styles.hint}>왼쪽은 묶음 제목, 가운데는 항목입니다. 항목은 한 줄에 하나씩 입력합니다.</p>
            </div>
          </div>

          <div className="section">
            <div className="sec-head">
              <div className="sec-title">대표이사 이력</div>
            </div>
            <div className="card">
              <div className={formStyles.grid} style={{ marginBottom: 14 }}>
                <div className={formStyles.field}>
                  <label>직함</label>
                  <input value={draft.ceoTitle} maxLength={50} onChange={(e) => setField('ceoTitle', e.target.value)} />
                </div>
                <div className={formStyles.field}>
                  <label>이름</label>
                  <input value={draft.ceoName} maxLength={50} onChange={(e) => setField('ceoName', e.target.value)} />
                </div>
              </div>
              <GroupListEditor
                groups={draft.career}
                onChange={(groups) => setField('career', groups)}
                labelPlaceholder="예: 학력"
              />
            </div>
          </div>

          <div className="section">
            <div className="sec-head">
              <div className="sec-title">디자인사업부</div>
            </div>
            <div className="card">
              <div className={styles.groupList}>
                {draft.services.map((service, index) => (
                  <div key={index} className={styles.groupRow}>
                    <input
                      value={service.title}
                      maxLength={100}
                      placeholder="예: BRANDING"
                      onChange={(e) => updateService(index, { title: e.target.value })}
                    />
                    <input
                      value={service.desc}
                      maxLength={300}
                      placeholder="설명"
                      onChange={(e) => updateService(index, { desc: e.target.value })}
                    />
                    <RowButtons
                      index={index}
                      length={draft.services.length}
                      onMove={(offset) => setField('services', move(draft.services, index, offset))}
                      onRemove={() => setField('services', draft.services.filter((_, i) => i !== index))}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className={`btn btn-outline ${styles.addBtn}`}
                  onClick={() => setField('services', [...draft.services, { title: '', desc: '' }])}
                >
                  + 업무 영역 추가
                </button>
              </div>
            </div>
          </div>

          {notice && <div className="card">{notice}</div>}

          <div className={formStyles.submitRow}>
            <button type="submit" className="btn btn-primary" disabled={isBusy}>
              {isBusy ? '저장 중...' : '저장하기'}
            </button>
          </div>
        </form>
      )}
    </AdminLayout>
  );
}

export default AdminCompanyHistory;
